/* eslint-disable react-hooks/exhaustive-deps */
import { useGetFromURLQuery } from "@/api/AppApi/services/builder";
import {
  IField,
  IKeyValue,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import { useEffect, useRef, useState, useMemo } from "react";
import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import { Dropdown, DropdownItem, Row } from "@/utils/bootstrap";
import CustomDropdownTitle from "./CustomDropdownTitle";
import InputErrorMsg from "../../form-builder-helpers/InputErrorMsg";
import SearchDropdownTitle from "./SearchDropdownTitle";
import RequiredStar from "@/app/components/lib/RequiredStar";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props extends IField {
  isPC?: boolean;
  form: UseFormReturn<FieldValues, any>;
  isSearch?: boolean;
  isGetKeyValue?: boolean;
  customOnChange?: (value: any) => void;
  className?: string;
  id?: string;
}
const CustomDropdown: React.FC<Props> = ({
  isPC = false,
  form,
  isSearch = false,
  isGetKeyValue,
  customOnChange,
  className,
  id,
  ...field
}) => {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const fConfig = field.configurations;

  const formValue = form.watch(fConfig.key);
  const selectedKey = useMemo(() => {
    if (isGetKeyValue) {
      return formValue?.key ?? "-1";
    }

    return formValue ?? "-1";
  }, [formValue, isGetKeyValue]);

  const parentKey = fConfig.parent_key
    ? isGetKeyValue
      ? (form.watch(fConfig.parent_key)?.key ?? "-1")
      : (form.watch(fConfig.parent_key) ?? "-1")
    : null;
  const getOptions = useGetFromURLQuery({
    url: fConfig.options_url ?? "",
    parentName: fConfig.parent_key?.split("(")?.pop() ?? "",
    parentKey: parentKey,
  });

  const { options, title, disabled } = useMemo(() => {
    let opts: IKeyValue[] = [];
    let isDisabled: boolean;

    if (field.data && field.data.length > 0) {
      opts = field?.data;
      isDisabled = false;
    } else {
      opts = getOptions?.data?.result ?? [];
      isDisabled = parentKey === "-1";
    }

    const calculatedTitle =
      selectedKey === "-1" || !selectedKey || selectedKey === undefined
        ? `${fConfig.placeholder ?? fConfig?.title}`
        : (opts.find((option) => option.key == selectedKey)?.value ??
          `${fConfig.placeholder ?? fConfig?.title}`);

    return {
      options: opts,
      title: calculatedTitle,
      disabled: isDisabled,
    };
  }, [
    field.data,
    getOptions?.data?.result,
    selectedKey,
    parentKey,
    fConfig.placeholder,
    fConfig.title,
  ]);

  const currentFormValue = form.getValues(fConfig.key);
  const valueToUse = useMemo(() => {
    if (isGetKeyValue) {
      return currentFormValue && currentFormValue?.key !== "-1"
        ? currentFormValue?.key
        : (field.value ?? "-1");
    }
    return currentFormValue && currentFormValue !== "-1"
      ? currentFormValue
      : (field.value ?? "-1");
  }, [currentFormValue, field.value]);

  const initValue = useMemo(() => {
    return valueToUse && valueToUse !== "-1"
      ? options.find((op) => op.key == valueToUse) || { key: "-1", value: "" }
      : { key: "-1", value: "" };
  }, [valueToUse, options]);

  const [searchTerm, setSearchTerm] = useState(initValue?.value || "");

  // Sync searchTerm with form value changes
  useEffect(() => {
    if (initValue?.value !== searchTerm) {
      setSearchTerm(initValue?.value || "");
    }
  }, [initValue?.value]);

  // Auto-select when only one option exists
  useEffect(() => {
    if (options?.length === 1) {
      setTimeout(() =>
        form.setValue(
          fConfig.key,
          isGetKeyValue ? options[0] : options[0]?.key,
        ),
      );
    }
  }, [options, getOptions.isLoading]);

  // Sync searchTerm when field.value changes (for isSearch mode)
  useEffect(() => {
    if (!isSearch) return;
    setSearchTerm(initValue?.value || "");
  }, [field.value]);

  // Reset value when parent changes and current value is invalid
  useEffect(() => {
    if (!parentKey || !getOptions?.isFetched) return;

    const valueExists = getOptions?.data?.result?.some(
      (op) => op.key == selectedKey,
    );

    if (parentKey === "-1" || (!valueExists && options?.length > 0)) {
      form.setValue(
        fConfig.key,
        isGetKeyValue ? { key: "-1", value: "" } : "-1",
      );
    }
  }, [parentKey, getOptions?.isFetched]);

  // Reset form value when search term is cleared by user
  useEffect(() => {
    if (isSearch && searchTerm === "" && isTyping) {
      form.setValue(
        fConfig.key,
        isGetKeyValue ? { key: "-1", value: "" } : "-1",
      );
    }
  }, [searchTerm, isSearch, isTyping]);

  // Sync value when parent changes (for isSearch mode with parent_key)
  useEffect(() => {
    if (!isSearch || !fConfig.parent_key) return;

    if (initValue?.key && initValue.key !== "-1") {
      form.setValue(
        fConfig.key,
        isGetKeyValue
          ? { key: initValue.key, value: initValue.value || "" }
          : initValue.key,
      );
      setSearchTerm(initValue?.value || "");
    }
  }, [form.watch(fConfig.parent_key ?? ""), isSearch, fConfig.parent_key]);

  const isSelectedRef = useRef(false);
  const getSearchableStrings = (op: IKeyValue): string[] => {
    const values = [op.value];
    if (op.meta?.name) {
      try {
        if (op.meta.name.trim().startsWith("{")) {
          const parsed = JSON.parse(op.meta.name);
          if (parsed.en) values.push(parsed.en);
          if (parsed.ar) values.push(parsed.ar);
        } else {
          values.push(op.meta.name);
        }
      } catch (e) {
        values.push(op.meta.name);
      }
    }
    return values;
  };

  // Filter options based on search term only when user is actively typing
  const optionsToRender = useMemo(() => {
    if (!isSearch || !isTyping) return options ?? [];
    return (options ?? []).filter(
      (op) =>
        getSearchableStrings(op).some((s) =>
          s.toLowerCase().includes((searchTerm || "").toLowerCase()),
        ) || op.value.toLowerCase().includes((searchTerm || "").toLowerCase()),
    );
  }, [isSearch, isTyping, options, searchTerm]);

  // Handle validation when dropdown closes without a selection
  const handleCloseValidation = () => {
    if (!isSearch) return;

    if (searchTerm !== "") {
      const selectedOption = options?.find(
        (op) =>
          getSearchableStrings(op).some((s) =>
            s.toLowerCase().includes((searchTerm || "").toLowerCase()),
          ) ||
          op.value.toLowerCase().includes((searchTerm || "").toLowerCase()),
      );
      if (!selectedOption) {
        // Revert to the previously selected value
        form.setValue(
          fConfig.key,
          isGetKeyValue
            ? initValue?.key
              ? {
                  key: initValue.key,
                  value: initValue.value || "",
                }
              : { key: "-1", value: "" }
            : (initValue?.key ?? "-1"),
        );
        setSearchTerm(initValue?.value || "");
      } else {
        // User typed an exact match, select it
        setSearchTerm(selectedOption?.value || "");
        form.setValue(
          fConfig.key,
          isGetKeyValue ? selectedOption : selectedOption?.key,
        );
      }
    }
  };

  // Handle search term changes - open dropdown when typing
  const handleSearchTermChange = (val: string) => {
    setSearchTerm(val || "");
    setIsTyping(true);
    // Open dropdown when user starts typing
    if (val && !isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div
      className={`${isSearch && "with-search-dropdown"} sort-dropdown w-100 ${
        isPC ? "pc" : ""
      } ${className ?? ""}`}
      id={id}
    >
      {!fConfig.hideLabel && (
        <RequiredStar title={fConfig.title} isRequired={fConfig.is_required} />
      )}
      <Controller
        control={form.control}
        name={fConfig.key}
        rules={{
          validate: (value) =>
            fConfig.is_required && (value === "-1" || !value)
              ? t.this_field_is_required
              : true,
          required: true,
        }}
        render={({ field: renderField }) => {
          return (
            <Dropdown
              show={isOpen}
              onToggle={(nextOpen) => {
                // When closing
                if (!nextOpen) {
                  // Reset typing state so all options show on next open
                  setIsTyping(false);
                  // If a selection was just made, skip validation
                  if (isSelectedRef.current) {
                    isSelectedRef.current = false;
                  } else {
                    handleCloseValidation();
                  }
                }
                setIsOpen(nextOpen);
              }}
            >
              <Dropdown.Toggle
                disabled={disabled}
                variant="outline"
                {...renderField}
              >
                {isSearch ? (
                  <SearchDropdownTitle
                    placeholder={title}
                    searchTerm={searchTerm || ""}
                    setSearchTerm={handleSearchTermChange}
                  />
                ) : (
                  <CustomDropdownTitle title={title} disabled={disabled} />
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {fConfig.parent_key && parentKey === "-1" ? (
                  <DropdownItem disabled>
                    <Row className="m-0 text-center text-muted">
                      {t.no_results}
                    </Row>
                  </DropdownItem>
                ) : (!field.data || field.data.length === 0) &&
                  getOptions.isLoading ? (
                  <DropdownItem disabled>
                    <Row className="m-0 text-center text-muted">
                      {t.loading}
                    </Row>
                  </DropdownItem>
                ) : optionsToRender.length === 0 && isSearch ? (
                  <DropdownItem disabled>
                    <Row className="m-0 text-center text-muted">
                      {t.no_results}
                    </Row>
                  </DropdownItem>
                ) : (
                  optionsToRender.map((option, index) => (
                    <DropdownItem
                      active={option?.key === selectedKey}
                      key={option.value + option.key + index}
                      onClick={() => {
                        isSelectedRef.current = true;
                        setSearchTerm(option.value || "");
                        renderField.onChange(
                          isGetKeyValue ? option : option.key,
                        );
                        if (!!customOnChange)
                          customOnChange(isGetKeyValue ? option : option.key);
                      }}
                    >
                      <Row className="m-0">{option?.value}</Row>
                    </DropdownItem>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          );
        }}
      />
      {fConfig.hint && <div className="field-hint">{fConfig.hint}</div>}
      <InputErrorMsg
        errorMsg={form.formState.errors[fConfig.key]?.message as string}
      />
    </div>
  );
};

export default CustomDropdown;
